/**
 * Render-time unit conversion for the user's preferred unit system.
 *
 * The recipe pipeline stores whatever unit the AI emitted (cup, tsp, oz, g…).
 * This converter remaps those at display time based on the viewer's
 * `profiles.preferred_unit_system`, so a metric viewer never sees "1 cup" and
 * an imperial viewer never sees "240 ml".
 *
 * Scope (deliberate):
 * - Volume → volume, mass → mass within the same dimension.
 * - No cross-dimension conversion (cup → g) — that needs density and risks
 *   noisy rounding; left for a later iteration.
 * - Count units (pc, clove, slice, pinch, bunch) are passed through untouched.
 * - Spoon units (tsp, tbsp) are passed through untouched — see below.
 *
 * Target abbreviations are the canonical set defined in unit-normalizer.ts.
 */

export type UnitSystem = 'metric' | 'imperial';

export interface QuantityWithUnit {
  /** Numeric quantity. May be null (count ingredients with no qty). */
  qty: number | null;
  /** Canonical abbreviation, or null/empty when no unit applies. */
  unit: string | null;
}

/**
 * Spoon measures belong to NO system — every cuisine measures seasonings with
 * them and every locale has its own name for the same spoon (`tsp`/`tbsp` →
 * `TL`/`EL` in unit-label.ts). Converting them is destructive in both
 * directions: "1¼ tsp salt" is how a cook works, "6.25 ml salt" is how nobody
 * does, and the generation prompt's SEASONINGS rule deliberately stores spices
 * in exactly these units. So they render as stored, whatever the viewer's
 * system — only the RECIPROCAL direction uses them as a conversion target
 * (fromMlToImperial), where an imperial viewer genuinely asked for spoons.
 *
 * `cup` and `fl oz` are NOT in this set: those are US measures a metric cook
 * has no vessel for, and converting them to ml is the whole point of this
 * module.
 */
const SPOON_VOLUME = new Set(['tsp', 'tbsp']);
const IMPERIAL_VOLUME = new Set(['cup', 'fl oz']);
const METRIC_VOLUME = new Set(['ml', 'l']);
const IMPERIAL_MASS = new Set(['oz', 'lb']);
const METRIC_MASS = new Set(['g', 'kg']);

// Reference: 1 US cup = 240 ml (cooking convention; not the exact 236.588).
const ML_PER_CUP = 240;
const ML_PER_TBSP = 15;
const ML_PER_TSP = 5;
const ML_PER_FL_OZ = 30;
const G_PER_OZ = 28.35;
const G_PER_LB = 453.6;

function roundTo(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/** Snap a value to a "nice" cooking-friendly precision. */
function nicelyRound(value: number, unit: string): number {
  if (unit === 'ml') {
    if (value < 10) return roundTo(value, 0.5);
    if (value < 100) return roundTo(value, 5);
    return roundTo(value, 10);
  }
  if (unit === 'l') return Math.round(value * 100) / 100; // 2dp
  if (unit === 'g') {
    if (value < 50) return roundTo(value, 1);
    if (value < 500) return roundTo(value, 5);
    return roundTo(value, 10);
  }
  if (unit === 'kg') return Math.round(value * 100) / 100;
  if (unit === 'oz') return Math.round(value * 4) / 4; // ¼ oz
  if (unit === 'lb') return Math.round(value * 100) / 100;
  if (unit === 'cup') return Math.round(value * 8) / 8;  // ⅛ cup
  if (unit === 'tbsp' || unit === 'tsp') return Math.round(value * 4) / 4; // ¼
  if (unit === 'fl oz') return Math.round(value * 2) / 2;
  return Math.round(value * 100) / 100;
}

/** Only the genuinely-imperial volumes convert; spoons short-circuit earlier. */
function toMetricVolumeMl(qty: number, unit: string): number | null {
  switch (unit) {
    case 'cup': return qty * ML_PER_CUP;
    case 'fl oz': return qty * ML_PER_FL_OZ;
    default: return null;
  }
}

function toMetricMassG(qty: number, unit: string): number | null {
  switch (unit) {
    case 'oz': return qty * G_PER_OZ;
    case 'lb': return qty * G_PER_LB;
    default: return null;
  }
}

function fromMlToImperial(ml: number): { qty: number; unit: string } {
  if (ml < ML_PER_TBSP) return { qty: ml / ML_PER_TSP, unit: 'tsp' };
  if (ml < ML_PER_CUP) return { qty: ml / ML_PER_TBSP, unit: 'tbsp' };
  return { qty: ml / ML_PER_CUP, unit: 'cup' };
}

function fromGToImperial(g: number): { qty: number; unit: string } {
  if (g < G_PER_LB) return { qty: g / G_PER_OZ, unit: 'oz' };
  return { qty: g / G_PER_LB, unit: 'lb' };
}

/**
 * Promote a metric value to a larger unit when the magnitude warrants it
 * (e.g. 1500 ml → 1.5 l, 1500 g → 1.5 kg).
 */
function promoteMetric(qty: number, unit: 'ml' | 'g'): { qty: number; unit: string } {
  if (unit === 'ml' && qty >= 1000) return { qty: qty / 1000, unit: 'l' };
  if (unit === 'g' && qty >= 1000) return { qty: qty / 1000, unit: 'kg' };
  return { qty, unit };
}

/**
 * Convert a stored (qty, unit) pair into the viewer's preferred system.
 * Pass-through when:
 *   - unit is null/empty or a count unit (pc, clove, slice, pinch, bunch)
 *   - qty is null
 *   - the unit already matches the target system
 */
export function convertForDisplay(
  qty: number | null,
  unit: string | null,
  target: UnitSystem,
): QuantityWithUnit {
  if (qty == null) return { qty, unit };
  if (!unit) return { qty, unit };

  const u = unit.toLowerCase().trim();

  // System-neutral spoon measures — kept as stored for every viewer.
  if (SPOON_VOLUME.has(u)) return { qty: nicelyRound(qty, u), unit: u };

  // Already in the target system → just promote large metric values.
  if (target === 'metric' && METRIC_VOLUME.has(u)) {
    if (u === 'ml') {
      const promoted = promoteMetric(qty, 'ml');
      return { qty: nicelyRound(promoted.qty, promoted.unit), unit: promoted.unit };
    }
    return { qty: nicelyRound(qty, u), unit: u };
  }
  if (target === 'metric' && METRIC_MASS.has(u)) {
    if (u === 'g') {
      const promoted = promoteMetric(qty, 'g');
      return { qty: nicelyRound(promoted.qty, promoted.unit), unit: promoted.unit };
    }
    return { qty: nicelyRound(qty, u), unit: u };
  }
  if (target === 'imperial' && (IMPERIAL_VOLUME.has(u) || IMPERIAL_MASS.has(u))) {
    return { qty: nicelyRound(qty, u), unit: u };
  }

  // Cross-system conversions
  if (target === 'metric') {
    if (IMPERIAL_VOLUME.has(u)) {
      const ml = toMetricVolumeMl(qty, u);
      if (ml == null) return { qty, unit };
      const promoted = promoteMetric(ml, 'ml');
      return { qty: nicelyRound(promoted.qty, promoted.unit), unit: promoted.unit };
    }
    if (IMPERIAL_MASS.has(u)) {
      const g = toMetricMassG(qty, u);
      if (g == null) return { qty, unit };
      const promoted = promoteMetric(g, 'g');
      return { qty: nicelyRound(promoted.qty, promoted.unit), unit: promoted.unit };
    }
  } else {
    if (METRIC_VOLUME.has(u)) {
      const ml = u === 'l' ? qty * 1000 : qty;
      const conv = fromMlToImperial(ml);
      return { qty: nicelyRound(conv.qty, conv.unit), unit: conv.unit };
    }
    if (METRIC_MASS.has(u)) {
      const g = u === 'kg' ? qty * 1000 : qty;
      const conv = fromGToImperial(g);
      return { qty: nicelyRound(conv.qty, conv.unit), unit: conv.unit };
    }
  }

  // Count units, pinches, unknown — pass through.
  return { qty, unit };
}
